import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import {
  SetProposalDiscountInput,
  SetProposalDiscountMutation,
} from '@/gql/graphql';

const SET_PROPOSAL_DISCOUNT_MUTATION = graphql(`
  mutation SetProposalDiscount($input: SetProposalDiscountInput!) {
    setProposalDiscount(input: $input) {
      id
    }
  }
`);

/**
 * Set the Proposal-level discount. The discount is applied uniformly to every
 * line item, so its effects span the Proposal's totals and the Project's profit
 * breakdown — both caches are invalidated on success.
 */
const useSetProposalDiscount = (args?: {
  proposalId?: string;
  projectId?: string;
  options?: UseMutationOptions<
    SetProposalDiscountMutation,
    unknown,
    SetProposalDiscountInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: SetProposalDiscountInput) => {
      return gqlClient.request(SET_PROPOSAL_DISCOUNT_MUTATION, { input });
    },
    onSuccess: (data, variables, context) => {
      if (args?.proposalId) {
        void queryClient.invalidateQueries({
          queryKey: proposalKeys.proposal(args.proposalId),
        });
      }
      if (args?.projectId) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.profitBreakdown(args.projectId),
        });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useSetProposalDiscount;
